import { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import LangInput from "@/components/LangInput";
import {
  Certificate, MapPin, GraduationCap, ArrowRight, Question,
  PaperPlaneTilt, PenNib, ShareNetwork, Link as LinkIcon, HandHeart,
  Books, Users, Sparkle, ImageSquare,
} from "@phosphor-icons/react";

const LEVEL_LABEL = {
  expert: "NaturePulse Expert",
  trusted: "Trusted Expert",
  verified: "Verified Expert",
  institutional: "Institutional Expert",
  fellow: "NaturePulse Fellow",
};

const TABS = [
  { key: "all", label: "Home" },
  { key: "personal_story", label: "Articles" },
  { key: "collections", label: "Collections" },
  { key: "research", label: "Research" },
  { key: "observation", label: "Observations" },
  { key: "photo_story", label: "Photos" },
  { key: "video", label: "Videos" },
  { key: "news_link", label: "Links" },
  { key: "question", label: "Q&A" },
];

// Post-only tabs (story_type filters) — collections & research bypass the posts feed.
const POST_TAB_KEYS = new Set(["all", "personal_story", "observation", "photo_story", "video", "news_link", "question"]);

export default function ExpertProfile() {
  const { slug } = useParams();
  const { user, requireSignIn } = useAuth();
  const [state, setState] = useState({ loading: true, expert: null, stats: {}, is_following: false });
  const [posts, setPosts] = useState({ items: [], total: 0, loading: false });
  const [collections, setCollections] = useState({ items: [], loading: false });
  const [researchRequests, setResearchRequests] = useState({ items: [], loading: false });
  const [tab, setTab] = useState("all");
  const [asking, setAsking] = useState(false);
  const [askOpen, setAskOpen] = useState(false);
  const [q, setQ] = useState("");

  const load = () => {
    setState((s) => ({ ...s, loading: true }));
    http.get(`/experts/${slug}`)
      .then(({ data }) => setState({ loading: false, ...data }))
      .catch(() => setState({ loading: false, expert: null }));
  };
  useEffect(load, [slug]);

  useEffect(() => {
    if (!state.expert) return;
    if (POST_TAB_KEYS.has(tab)) {
      setPosts((p) => ({ ...p, loading: true }));
      const params = { limit: 30 };
      if (tab !== "all") params.story_type = tab;
      http.get(`/experts/${slug}/posts`, { params })
        .then(({ data }) => setPosts({ items: data.posts || [], total: data.total || 0, loading: false }))
        .catch(() => setPosts({ items: [], total: 0, loading: false }));
      return;
    }
    if (tab === "collections") {
      setCollections({ items: [], loading: true });
      http.get(`/experts/${slug}/collections`)
        .then(({ data }) => setCollections({ items: data.collections || [], loading: false }))
        .catch(() => setCollections({ items: [], loading: false }));
    }
    if (tab === "research") {
      setResearchRequests({ items: [], loading: true });
      http.get("/experts/research-requests", { params: { limit: 40, status: "all" } })
        .then(({ data }) => {
          const mine = (data.requests || []).filter((r) => r.expert_slug === slug);
          setResearchRequests({ items: mine, loading: false });
        })
        .catch(() => setResearchRequests({ items: [], loading: false }));
    }
  }, [slug, tab, state.expert]);

  const toggleFollow = async () => {
    if (!requireSignIn("follow experts")) return;
    try {
      if (state.is_following) {
        await http.delete(`/experts/${state.expert.id}/follow`);
        setState((s) => ({ ...s, is_following: false, stats: { ...s.stats, followers: (s.stats?.followers || 1) - 1 } }));
        toast.success("Unfollowed");
      } else {
        await http.post(`/experts/${state.expert.id}/follow`);
        setState((s) => ({ ...s, is_following: true, stats: { ...s.stats, followers: (s.stats?.followers || 0) + 1 } }));
        toast.success("Following");
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };

  const askQuestion = async () => {
    if (!requireSignIn("ask an expert")) return;
    if (q.trim().length < 6) return toast.error("Question must be at least 6 characters.");
    setAsking(true);
    try {
      await http.post(`/experts/${state.expert.id}/ask`, { question: q.trim() });
      toast.success("Question sent — the expert will decide whether to answer.");
      setAskOpen(false); setQ("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send question"); }
    finally { setAsking(false); }
  };

  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) await navigator.share({ title: state.expert?.display_name, url });
      else { await navigator.clipboard.writeText(url); toast.success("Link copied"); }
    } catch { /* user cancelled */ }
  };

  const canonicalLocation = useMemo(() => {
    const l = state.expert?.location || {};
    return [l.city, l.state, l.country].filter(Boolean).join(", ");
  }, [state.expert]);

  if (state.loading) {
    return <main className="bg-[#F5EBDC] min-h-screen pt-32 md:pt-36 pb-32 px-5 md:px-10"><div className="max-w-[1100px] mx-auto animate-pulse h-64 bg-white/60 rounded-2xl" /></main>;
  }
  if (!state.expert) {
    return (
      <main className="bg-[#F5EBDC] min-h-screen pt-32 md:pt-36 pb-32 px-5 md:px-10">
        <div className="max-w-[720px] mx-auto text-center">
          <h1 className="font-serif-display text-3xl">Expert not found</h1>
          <p className="mt-2 text-sm text-[#3A3830]">This Expert Space doesn't exist or has been retired.</p>
          <Link to="/experts" className="mt-4 inline-flex text-[color:var(--np-brand,#B85F2A)] underline">Browse the directory</Link>
        </div>
      </main>
    );
  }

  const e = state.expert;
  return (
    <main className="bg-[#F5EBDC] min-h-screen pt-32 md:pt-36 pb-32 px-5 md:px-10" data-testid="expert-profile">
      <div className="max-w-[1100px] mx-auto">
        {/* Hero */}
        <div className="rounded-2xl bg-white border border-black/10 p-6 md:p-8 shadow-sm">
          <div className="flex items-start gap-4 md:gap-6 flex-wrap">
            <div className="w-24 h-24 md:w-28 md:h-28 rounded-full bg-[#F5EBDC] overflow-hidden grid place-items-center flex-shrink-0">
              {e.profile_photo ? (
                <img src={e.profile_photo} alt={e.display_name} className="w-full h-full object-cover" />
              ) : (
                <PenNib size={32} weight="fill" className="text-[color:var(--np-brand,#B85F2A)]" />
              )}
            </div>
            <div className="flex-1 min-w-[240px]">
              <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)] flex items-center gap-2">
                {LEVEL_LABEL[e.level] || "NaturePulse Expert"}
                {e.verified && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-300 text-emerald-800 px-1.5 py-[1px] text-[9px]">
                    <Certificate size={9} weight="fill" /> Verified
                  </span>
                )}
              </div>
              <h1 className="mt-1 font-serif-display text-3xl md:text-4xl leading-tight" data-testid="expert-name">{e.display_name || e.name}</h1>
              {e.professional_title && <div className="mt-1 text-sm text-[#3A3830]">{e.professional_title}</div>}
              <div className="mt-2 flex items-center gap-3 flex-wrap text-xs text-[#3A3830]">
                {e.institution && (<span className="inline-flex items-center gap-1"><GraduationCap size={11} weight="fill" /> {e.institution}</span>)}
                {canonicalLocation && (<span className="inline-flex items-center gap-1"><MapPin size={11} weight="fill" /> {canonicalLocation}</span>)}
                {e.languages?.length > 0 && <span className="uppercase font-mono-tech tracking-[0.2em] text-[10px]">Speaks: {e.languages.join(" · ")}</span>}
              </div>
              {e.short_bio && <p className="mt-4 text-sm text-[#3A3830]">{e.short_bio}</p>}
              {e.expertise?.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {e.expertise.map((t) => (
                    <Link key={t} to={`/experts?topic=${encodeURIComponent(t)}`} className="rounded-full bg-[#FBF2E1] border border-black/10 px-2.5 py-0.5 font-mono-tech text-[9px] tracking-[0.22em] uppercase hover:border-black/40">{t}</Link>
                  ))}
                </div>
              )}
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <button
                  onClick={toggleFollow}
                  data-testid="expert-follow-btn"
                  className={`inline-flex items-center gap-2 rounded-full px-5 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase ${
                    state.is_following
                      ? "bg-white border border-black/15 text-[#241209]"
                      : "bg-[#151312] text-[#F5EBDC]"
                  }`}
                >
                  <HandHeart size={12} weight="fill" /> {state.is_following ? "Following" : "Follow"}
                </button>
                {e.accepting_questions && (
                  <button
                    onClick={() => { if (requireSignIn("ask an expert")) setAskOpen(true); }}
                    data-testid="expert-ask-btn"
                    className="inline-flex items-center gap-2 rounded-full border border-[color:var(--np-brand,#B85F2A)] bg-white text-[color:var(--np-brand,#B85F2A)] px-5 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:bg-[color:var(--np-brand,#B85F2A)] hover:text-white transition"
                  >
                    <Question size={12} weight="fill" /> Ask an expert
                  </button>
                )}
                <button
                  onClick={share}
                  className="inline-flex items-center gap-2 rounded-full border border-black/15 bg-white px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:bg-black/5"
                  data-testid="expert-share"
                >
                  <ShareNetwork size={12} weight="fill" /> Share
                </button>
                <span className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830] ml-2" data-testid="expert-stats">
                  {state.stats?.posts || 0} posts · {state.stats?.followers || 0} followers
                </span>
              </div>
              {e.external_links?.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {e.external_links.map((l) => (
                    <a key={l.url} href={l.url} target="_blank" rel="noreferrer noopener" className="inline-flex items-center gap-1 rounded-full bg-[#F5EBDC] border border-black/10 px-3 py-1 text-xs hover:border-black/40">
                      <LinkIcon size={11} weight="fill" /> {l.label}
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>
          {e.full_bio && (
            <div className="mt-6 pt-6 border-t border-black/10">
              <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-muted,#3A3830)]">About</div>
              <p className="mt-2 text-sm text-[#3A3830] whitespace-pre-wrap leading-relaxed">{e.full_bio}</p>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="mt-8 flex gap-1.5 flex-wrap border-b border-black/10 pb-1" data-testid="expert-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`expert-tab-${t.key}`}
              className={`rounded-t-lg px-3 py-1.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase ${
                tab === t.key
                  ? "bg-white border-l border-r border-t border-black/15 text-[color:var(--np-brand,#B85F2A)]"
                  : "text-[#3A3830] hover:bg-white/60"
              }`}
            >{t.label}</button>
          ))}
          {user?.firebase_uid === e.id && (
            <Link
              to="/dashboard/expert"
              className="ml-auto rounded-t-lg px-3 py-1.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] border border-dashed border-[color:var(--np-brand,#B85F2A)]"
              data-testid="expert-my-dashboard"
            >
              Go to your dashboard
            </Link>
          )}
        </div>

        {/* Posts grid */}
        <div className="mt-6" data-testid="expert-content">
          {tab === "collections" && (
            <CollectionsGrid loading={collections.loading} items={collections.items} />
          )}
          {tab === "research" && (
            <ResearchGrid loading={researchRequests.loading} items={researchRequests.items} />
          )}
          {POST_TAB_KEYS.has(tab) && (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="expert-posts">
              {posts.loading && Array.from({ length: 6 }).map((_, i) => <div key={i} className="rounded-2xl border border-black/10 bg-white/60 h-48 animate-pulse" />)}
              {!posts.loading && posts.items.length === 0 && (
                <div className="col-span-full text-sm text-[#3A3830] py-8 text-center">
                  No {tab === "all" ? "posts" : TABS.find((x) => x.key === tab)?.label.toLowerCase()} yet.
                </div>
              )}
              {posts.items.map((p) => (
                <Link
                  key={p.id}
                  to={`/stories/${p.id}`}
                  data-testid={`expert-post-${p.id}`}
                  className="rounded-2xl border border-black/10 bg-white p-5 hover:border-[color:var(--np-brand,#B85F2A)] transition"
                >
                  <div className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)]">
                    {(p.story_type || "").replace(/_/g, " ")}{p.category ? ` · ${p.category}` : ""}
                  </div>
                  <h3 className="mt-1 font-serif-display text-lg leading-tight line-clamp-2">{p.title || p.headline || "Untitled"}</h3>
                  {(p.summary || p.body) && <p className="mt-1 text-xs text-[#3A3830] line-clamp-3">{p.summary || p.body}</p>}
                  <div className="mt-3 text-[10px] font-mono-tech tracking-[0.22em] uppercase text-[#3A3830] flex items-center gap-1">
                    Read <ArrowRight size={10} weight="bold" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Ask an Expert modal */}
      {askOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center px-4" data-testid="expert-ask-modal">
          <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl text-[#151312]">
            <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">Ask an expert</div>
            <h3 className="mt-1 font-serif-display text-2xl leading-tight">Ask {e.display_name || e.name}</h3>
            <p className="mt-2 text-xs text-[#3A3830]">Not every question receives a reply — but the expert may turn a valuable answer into a full article, credited to your question.</p>
            <div className="mt-4">
              <LangInput as="textarea" rows={4} value={q} onChange={(ev) => setQ(ev.target.value)} placeholder="What's on your mind?" testid="expert-ask-input" />
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setAskOpen(false)} className="rounded-full border border-black/15 bg-white px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:bg-black/5">Cancel</button>
              <button
                onClick={askQuestion}
                disabled={asking}
                data-testid="expert-ask-send"
                className="inline-flex items-center gap-2 rounded-full bg-[#151312] text-[#F5EBDC] px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase disabled:opacity-50"
              >
                <PaperPlaneTilt size={12} weight="fill" /> {asking ? "Sending…" : "Send"}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

function CollectionsGrid({ loading, items }) {
  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {Array.from({ length: 3 }).map((_, i) => <div key={i} className="rounded-2xl border border-black/10 bg-white/60 h-48 animate-pulse" />)}
      </div>
    );
  }
  if (items.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-black/20 p-10 text-center bg-white/70" data-testid="expert-collections-empty">
        <Books size={20} weight="fill" className="mx-auto text-[color:var(--np-brand,#B85F2A)] mb-2" />
        <div className="font-serif-display text-2xl">No collections yet.</div>
        <div className="mt-1 text-sm text-[#3A3830]">This expert hasn't curated any collections yet.</div>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="expert-collections">
      {items.map((c) => (
        <Link
          key={c.id}
          to={`/collections/${c.id}`}
          data-testid={`expert-collection-${c.id}`}
          className="rounded-2xl border border-black/10 bg-white overflow-hidden hover:border-[color:var(--np-brand,#B85F2A)] transition group"
        >
          {c.cover_image ? (
            <div className="aspect-[16/10] overflow-hidden bg-[#F5EBDC]">
              <img src={c.cover_image} alt={c.title} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
            </div>
          ) : (
            <div className="aspect-[16/10] bg-[#FBF2E1] grid place-items-center">
              <ImageSquare size={28} weight="fill" className="text-[color:var(--np-brand,#B85F2A)]/40" />
            </div>
          )}
          <div className="p-4">
            <div className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] flex items-center gap-1.5">
              <Books size={10} weight="fill" /> Collection · {c.items_count} {c.items_count === 1 ? "story" : "stories"}
              {c.topic && <><span className="text-[#3A3830]">·</span><span>{c.topic}</span></>}
            </div>
            <h3 className="mt-1 font-serif-display text-lg leading-tight line-clamp-2">{c.title}</h3>
            {c.description && <p className="mt-1 text-xs text-[#3A3830] line-clamp-3">{c.description}</p>}
          </div>
        </Link>
      ))}
    </div>
  );
}

function ResearchGrid({ loading, items }) {
  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {Array.from({ length: 2 }).map((_, i) => <div key={i} className="rounded-2xl border border-black/10 bg-white/60 h-40 animate-pulse" />)}
      </div>
    );
  }
  if (items.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-black/20 p-10 text-center bg-white/70" data-testid="expert-research-empty">
        <Users size={20} weight="fill" className="mx-auto text-[color:var(--np-brand,#B85F2A)] mb-2" />
        <div className="font-serif-display text-2xl">No active research requests.</div>
        <div className="mt-1 text-sm text-[#3A3830]">This expert isn't asking for community observations right now.</div>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="expert-research">
      {items.map((r) => (
        <div
          key={r.id}
          data-testid={`expert-research-${r.id}`}
          className="rounded-2xl border border-black/10 bg-white p-5 hover:border-[color:var(--np-brand,#B85F2A)] transition"
        >
          <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] flex items-center gap-1">
            <Users size={11} weight="fill" /> Research request
            {r.status !== "active" && <span className="ml-2 text-[#3A3830]">· {r.status}</span>}
          </div>
          <h3 className="mt-1 font-serif-display text-xl leading-tight">{r.title}</h3>
          <p className="mt-2 text-sm text-[#3A3830] line-clamp-4">{r.body}</p>
          {r.topics?.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {r.topics.slice(0, 4).map((t) => (
                <span key={t} className="rounded-full bg-[#FBF2E1] border border-black/10 px-2 py-0.5 font-mono-tech text-[9px] tracking-[0.22em] uppercase">{t}</span>
              ))}
            </div>
          )}
          <div className="mt-4 flex items-center justify-between">
            <span className="font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#3A3830]">
              {r.contributions_count || 0} contributions
            </span>
            <Link
              to="/stories/new"
              data-testid={`expert-research-contribute-${r.id}`}
              className="inline-flex items-center gap-1 rounded-full bg-[#151312] text-[#F5EBDC] px-3 py-1.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:opacity-90"
            >
              <Sparkle size={11} weight="fill" /> Contribute <ArrowRight size={10} weight="bold" />
            </Link>
          </div>
        </div>
      ))}
    </div>
  );
}

